/**
 * Quick Add to Cart - Collection Page AJAX Handler
 * 
 * Handles single-variant product quick add from collection pages with:
 * - Delegated click listener for [data-quick-add] buttons
 * - Duplicate click protection / debounce
 * - AbortController for cancellable requests
 * - Loading and error state transitions
 * - Toast notification on success
 * - Cart count updates (supports <cart-count> and [data-cart-count])
 * - GA4 add_to_cart event (fires only after success)
 */

(function() {
  'use strict';

  const CART_ADD_ENDPOINT = '/cart/add.js';
  const CART_ENDPOINT = '/cart.js';
  const TOAST_DURATION = 3000;
  const DEBOUNCE_MS = 300;

  let activeController = null;
  let lastClickTime = 0;
  let toastTimeout = null;

  /**
   * Show toast notification
   */
  function showToast(productTitle, productImage) {
    let toast = document.querySelector('.ks-toast--quick-add');
    
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'ks-toast ks-toast--quick-add';
      toast.innerHTML = `
        <span class="ks-toast__icon">✓</span>
        <span class="ks-toast__text"></span>
        <a href="/cart" class="ks-toast__action">View Cart →</a>
        <button type="button" class="ks-toast__close" aria-label="Close">&times;</button>
      `;
      document.body.appendChild(toast);
      
      toast.querySelector('.ks-toast__close').addEventListener('click', hideToast);
    }
    
    toast.querySelector('.ks-toast__text').textContent = `${productTitle} added to cart`;
    
    if (toastTimeout) {
      clearTimeout(toastTimeout);
    }
    
    requestAnimationFrame(() => {
      toast.classList.add('is-visible');
    });
    
    toastTimeout = setTimeout(hideToast, TOAST_DURATION);
  }

  /**
   * Hide toast notification
   */
  function hideToast() {
    const toast = document.querySelector('.ks-toast--quick-add');
    if (toast) {
      toast.classList.remove('is-visible');
    }
    if (toastTimeout) {
      clearTimeout(toastTimeout);
      toastTimeout = null;
    }
  }

  /**
   * Update cart count in header
   */
  function updateCartCount(count) {
    // Update <cart-count> custom elements (Prestige theme pattern)
    document.querySelectorAll('cart-count').forEach(el => {
      const countSpan = el.querySelector('.header__cart-count');
      if (countSpan) {
        countSpan.textContent = count;
        if (count > 0) {
          countSpan.removeAttribute('hidden');
        }
      }
      // Also try direct text content update
      if (el.dataset.cartCount !== undefined) {
        el.dataset.cartCount = count;
      }
    });
    
    // Update [data-cart-count] elements (fallback)
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = count;
      el.dataset.cartCount = count;
    });
  }

  /**
   * Fire GA4 add_to_cart event
   */
  function fireGA4(variantId, productTitle, price, quantity) {
    if (typeof window.dataLayer === 'undefined') return;
    
    // Clear previous ecommerce object
    window.dataLayer.push({ ecommerce: null });
    
    window.dataLayer.push({
      event: 'add_to_cart',
      ecommerce: {
        currency: 'GBP',
        value: price / 100,
        items: [{
          item_id: variantId,
          item_name: productTitle,
          price: price / 100,
          quantity: quantity
        }]
      }
    });
  }

  /**
   * Set button loading state
   */
  function setLoading(button, isLoading) {
    if (isLoading) {
      button.classList.add('ks-btn--loading');
      button.disabled = true;
      button.dataset.originalText = button.textContent;
      button.textContent = '';
    } else {
      button.classList.remove('ks-btn--loading');
      button.disabled = false;
      if (button.dataset.originalText) {
        button.textContent = button.dataset.originalText;
        delete button.dataset.originalText;
      }
    }
  }

  /**
   * Set button error state
   */
  function setError(button, message, isRetryable) {
    button.classList.remove('ks-btn--loading');
    button.textContent = message;
    
    if (isRetryable) {
      button.disabled = false;
      setTimeout(() => {
        if (button.dataset.originalText) {
          button.textContent = button.dataset.originalText;
          delete button.dataset.originalText;
        } else {
          button.textContent = 'Quick add';
        }
      }, 2000);
    } else {
      button.disabled = true;
    }
  }

  /**
   * Handle quick add click
   */
  async function handleQuickAdd(event) {
    const button = event.target.closest('[data-quick-add]');
    if (!button) return;
    
    event.preventDefault();
    event.stopPropagation();
    
    // Debounce rapid clicks
    const now = Date.now();
    if (now - lastClickTime < DEBOUNCE_MS) return;
    lastClickTime = now;
    
    // Cancel any pending request
    if (activeController) {
      activeController.abort();
    }
    activeController = new AbortController();
    
    const variantId = button.dataset.variantId;
    const productTitle = button.dataset.productTitle;
    const productPrice = parseInt(button.dataset.productPrice, 10);
    const productImage = button.dataset.productImage;
    
    if (!variantId) {
      console.error('Quick add: Missing variant ID');
      return;
    }
    
    setLoading(button, true);
    
    try {
      // Add to cart
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      
      const addResponse = await fetch(CART_ADD_ENDPOINT, {
        method: 'POST',
        body: formData,
        signal: activeController.signal
      });
      
      if (!addResponse.ok) {
        const errorData = await addResponse.json().catch(() => ({}));
        
        if (addResponse.status === 422) {
          // Out of stock or unavailable
          setError(button, 'Out of stock', false);
          return;
        }
        
        throw new Error(errorData.message || errorData.description || 'Add to cart failed');
      }
      
      // Success - fetch updated cart for count
      const cartResponse = await fetch(CART_ENDPOINT, {
        signal: activeController.signal
      });
      
      if (cartResponse.ok) {
        const cart = await cartResponse.json();
        updateCartCount(cart.item_count);
      }
      
      // Reset button
      setLoading(button, false);
      
      // Show toast
      showToast(productTitle, productImage);
      
      // Fire GA4 event (only after confirmed success)
      fireGA4(variantId, productTitle, productPrice, 1);
      
      // Dispatch custom event for any listeners
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { variantId, quantity: 1 }
      }));
      
    } catch (error) {
      if (error.name === 'AbortError') {
        // Request was cancelled, reset button quietly
        setLoading(button, false);
        return;
      }
      
      console.error('Quick add error:', error);
      setError(button, 'Try again', true);
    } finally {
      activeController = null;
    }
  }

  // Single delegated listener
  document.addEventListener('click', handleQuickAdd);
  
})();
